import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { supabase } from '../lib/db';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../components/AuthContext';
import { Calendar, ArrowLeft } from 'lucide-react';
import QRCode from 'react-qr-code';
import { notificationService } from '../services/NotificationService';
import { useTimeFormatStore } from '../store/timeFormatStore';
import { getEthiopianDateStr, getTimeStr } from '../utils/ethiopianCalendar';
import { getDayName } from '../lib/constants';

interface Service {
  id: string;
  name: string;
  description: string;
  duration: number;
  price: number;
  image_url?: string;
}

interface RouteParams {
  provider_id: string;
}

interface UserMetadata {
  full_name?: string;
  phone?: string;
}

interface User {
  id: string;
  email: string;
  user_metadata?: UserMetadata;
}

interface QueueEntry {
  counter_id: number;
  scheduled_start_time: string;
  services: {
    duration: number;
  };
}

interface QueueEntryResponse {
  counter_id: number;
  scheduled_start_time: string;
  services: {
    duration: number;
  }[];
}

interface BlockedTimeSlot {
  counter_id: number;
  time: string;
  date: string;
  reason?: string;
}

interface RecurringBlock {
  id: number;
  provider_id: string;
  counter_id: number | null;
  day_of_week: string | null;
  start_time: string;
  end_time: string;
  is_active: boolean;
  description?: string;
}

interface CounterDetails {
  id: number;
  name: string;
}

interface TimeSlotAvailability {
  time: string;
  availableCounters: number[];
}

interface BookingFlowProps {}

export const BookingFlow: React.FC<BookingFlowProps> = () => {
  const { provider_id } = useParams<RouteParams>();
  const history = useHistory();
  const { t, language } = useLanguage();
  const { user } = useAuth() as { user: User | null };
  const { timeFormat, setTimeFormat } = useTimeFormatStore();
  
  const [services, setServices] = useState<Service[]>([]);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [availableSlots, setAvailableSlots] = useState<TimeSlotAvailability[]>([]);
  const [selectedCounterId, setSelectedCounterId] = useState<number | null>(null);
  const [selectedCounterName, setSelectedCounterName] = useState<string>('');
  const [availableDates, setAvailableDates] = useState<Array<{
    date: string;
    dayName: string;
    dayOfMonth: number;
    isToday?: boolean;
  }>>([]);
  const [nextWeekDates, setNextWeekDates] = useState<Array<{
    date: string;
    dayName: string;
    dayOfMonth: number;
    isToday?: boolean;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingConfirmation, setBookingConfirmation] = useState<any>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showingNextWeek, setShowingNextWeek] = useState(false);

  useEffect(() => {
    if (provider_id) {
      fetchServices();
      generateAvailableDates();
    }
  }, [provider_id, language]);

  useEffect(() => {
    if (selectedService && selectedDate) {
      fetchAvailableSlots();
    }
  }, [selectedService, selectedDate]);

  const fetchServices = async () => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('provider_id', provider_id)
        .eq('is_active', true);

      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
      setError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const generateAvailableDates = async () => {
    try {
      // Get provider's availability for all days
      const { data: availabilityData, error: availabilityError } = await supabase
        .from('service_availability')
        .select('day_of_week')
        .eq('provider_id', provider_id)
        .eq('is_active', true);

      if (availabilityError) {
        console.error('Error fetching availability:', availabilityError);
        return;
      }

      const availableDaysOfWeek = availabilityData?.map(d => parseInt(d.day_of_week)) || [];
      
      // Get current date and start of week (Sunday)
      const today = new Date();
      const currentDayOfWeek = today.getDay();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - currentDayOfWeek);

      // Map day numbers to translation keys
      const dayToTransKey: { [key: number]: "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat" } = {
        0: 'sun',
        1: 'mon',
        2: 'tue',
        3: 'wed',
        4: 'thu',
        5: 'fri',
        6: 'sat'
      };

      // Generate dates for current week
      const currentWeekDates = [];
      const nextWeekDates = [];

      for (let i = 0; i < 14; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        
        // Skip dates in the past
        if (date < today) continue;

        const dayOfWeek = date.getDay();
        const isAvailableDay = availableDaysOfWeek.includes(dayOfWeek);

        if (!isAvailableDay) continue;

        const dateString = date.toISOString().split('T')[0];
        
        // Get translated day name using the full day name from constants
        const translatedDayName = getDayName(dayToTransKey[dayOfWeek as keyof typeof dayToTransKey], language as 'en' | 'am');
        
        // Check if date is in current week or next week
        if (i < 7) {
          currentWeekDates.push({
            date: dateString,
            dayName: translatedDayName,
            dayOfMonth: date.getDate(),
            isToday: date.toDateString() === today.toDateString()
          });
        } else {
          nextWeekDates.push({
            date: dateString,
            dayName: translatedDayName,
            dayOfMonth: date.getDate()
          });
        }
      }

      setAvailableDates(currentWeekDates);
      setNextWeekDates(nextWeekDates);
    } catch (error) {
      console.error('Error generating dates:', error);
      setError('Failed to load available dates');
    }
  };

  const parseTimeRange = (timeRange: string): { start: Date, end: Date } | null => {
    try {
      const [startStr, endStr] = timeRange.split('-');
      if (!startStr || !endStr) return null;

      const [startHour, startMinute] = startStr.split(':').map(num => parseInt(num));
      const [endHour, endMinute] = endStr.split(':').map(num => parseInt(num));

      const start = new Date();
      start.setHours(startHour, startMinute, 0, 0);
      
      const end = new Date();
      end.setHours(endHour, endMinute, 0, 0);

      return { start, end };
    } catch (error) {
      console.error('Error parsing time range:', timeRange, error);
      return null;
    }
  };

  const fetchAvailableSlots = async () => {
    if (!selectedService || !selectedDate) return;

    try {
      // Get server time in UTC
      const { data: serverTimeData } = await supabase.rpc('get_current_timestamp');
      const now = new Date(serverTimeData as any);

      const dayOfWeek = new Date(selectedDate).getDay();
      
      // First, get the counters that can provide this service
      const { data: serviceCounters } = await supabase
        .from('counter_services')
        .select(`
          counter_id,
          service_counters!inner (
            id,
            name
          )
        `)
        .eq('service_id', selectedService.id);

      if (!serviceCounters || serviceCounters.length === 0) {
        setAvailableSlots([]);
        return;
      }

      const counterIds = serviceCounters.map(sc => sc.counter_id);
      
      // Use 5-minute intervals for scanning but service duration for slot display
      const SCAN_INTERVAL = 5; // 5 minutes for scanning
      const serviceDuration = selectedService.duration || 15; // Service duration determines slot intervals

      // Get provider's availability for this day of week
      const { data: availabilityData } = await supabase
        .from('service_availability')
        .select('start_time, end_time')
        .eq('provider_id', provider_id)
        .eq('day_of_week', dayOfWeek)
        .eq('is_active', true)
        .single();

      if (!availabilityData) {
        setAvailableSlots([]);
        return;
      }

      // Parse provider availability times
      const [startHour, startMinute] = availabilityData.start_time.split(':').map((n: string) => Number(n));
      const [endHour, endMinute] = availabilityData.end_time.split(':').map((n: string) => Number(n));
      
      const availabilityStart = new Date(`${selectedDate}T00:00:00`);
      availabilityStart.setHours(startHour, startMinute, 0, 0);
      
      const availabilityEnd = new Date(`${selectedDate}T00:00:00`);
      availabilityEnd.setHours(endHour, endMinute, 0, 0);

      // Check if the selected date is today
      const selectedDateObj = new Date(`${selectedDate}T00:00:00`);
      const today = new Date();
      const isToday = selectedDateObj.toDateString() === today.toDateString();
      
      // If it's today, adjust the start time to not show past slots
      if (isToday) {
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        
        if (currentHour > startHour || (currentHour === startHour && currentMinute > startMinute)) {
          // Round up to the next service duration interval
          const roundedMinutes = Math.ceil(currentMinute / serviceDuration) * serviceDuration;
          availabilityStart.setHours(currentHour, roundedMinutes, 0, 0);
        }
      }

      // Fetch existing bookings for the selected date
      const startOfDay = new Date(`${selectedDate}T00:00:00`);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(`${selectedDate}T00:00:00`);
      endOfDay.setHours(23, 59, 59, 999);

      const { data: existingBookings } = await supabase
        .from('queue_entries')
        .select(`
          counter_id,
          scheduled_start_time,
          services!inner (
            duration
          )
        `)
        .gte('scheduled_start_time', startOfDay.toISOString())
        .lte('scheduled_start_time', endOfDay.toISOString())
        .in('status', ['scheduled', 'in_progress'])
        .in('counter_id', counterIds);

      // Fetch blocked time slots
      const { data: blockedSlots } = await supabase
        .from('blocked_time_slots')
        .select('*')
        .eq('provider_id', provider_id)
        .eq('date', selectedDate)
        .in('counter_id', counterIds);

      // Fetch recurring blocks
      const { data: recurringBlocks } = await supabase
        .from('recurring_blocks')
        .select('*')
        .eq('provider_id', provider_id)
        .eq('is_active', true);

      // Helper function to check if a time slot is available
      const isSlotAvailable = (startTime: Date, endTime: Date, counterId: number): boolean => {
        // Check existing bookings
        const hasBooking = existingBookings?.some((booking: QueueEntryResponse) => {
          const localBookingTimeStr = booking.scheduled_start_time.substring(0, 19);
          const bookingStart = new Date(localBookingTimeStr);
          const bookingEnd = new Date(bookingStart.getTime() + (booking.services[0]?.duration || 15) * 60000);
          return (
            booking.counter_id === counterId &&
            ((startTime >= bookingStart && startTime < bookingEnd) ||
             (endTime > bookingStart && endTime <= bookingEnd) ||
             (startTime <= bookingStart && endTime >= bookingEnd))
          );
        });

        if (hasBooking) return false;

        // Check specific blocks
        const isSpecificallyBlocked = blockedSlots?.some((block: BlockedTimeSlot) => {
          if (block.counter_id !== counterId) return false;
          const timeRange = parseTimeRange(block.time);
          if (!timeRange) return false;

          const blockStart = new Date(`${selectedDate}T00:00:00`);
          blockStart.setHours(timeRange.start.getHours(), timeRange.start.getMinutes(), 0, 0);
          
          const blockEnd = new Date(`${selectedDate}T00:00:00`);
          blockEnd.setHours(timeRange.end.getHours(), timeRange.end.getMinutes(), 0, 0);

          return (
            (startTime >= blockStart && startTime < blockEnd) ||
            (endTime > blockStart && endTime <= blockEnd) ||
            (startTime <= blockStart && endTime >= blockEnd)
          );
        });

        if (isSpecificallyBlocked) return false;

        // Check recurring blocks
        const isRecurringlyBlocked = recurringBlocks?.some((block: RecurringBlock) => {
          if (block.counter_id !== null && block.counter_id !== counterId) return false;
          
          if (block.day_of_week !== null && parseInt(block.day_of_week) !== dayOfWeek) return false;

          const [blockStartHour, blockStartMinute] = block.start_time.split(':').map(Number);
          const [blockEndHour, blockEndMinute] = block.end_time.split(':').map((num: string) => parseInt(num));
          
          const blockStart = new Date(`${selectedDate}T00:00:00`);
          blockStart.setHours(blockStartHour, blockStartMinute, 0, 0);
          
          const blockEnd = new Date(`${selectedDate}T00:00:00`);
          blockEnd.setHours(blockEndHour, blockEndMinute, 0, 0);

          if (blockStartHour > blockEndHour || (blockStartHour === blockEndHour && blockStartMinute > blockEndMinute)) {
            // Block goes past midnight
            const nextDayBlockEnd = new Date(blockEnd.getTime() + 24 * 60 * 60 * 1000);
            const prevDayBlockStart = new Date(blockStart.getTime() - 24 * 60 * 60 * 1000);
            return (
              (startTime >= blockStart && startTime < nextDayBlockEnd) ||
              (startTime >= prevDayBlockStart && startTime < blockEnd) ||
              (endTime > blockStart && endTime <= nextDayBlockEnd) ||
              (endTime > prevDayBlockStart && endTime <= blockEnd)
            );
          } else {
            return (
              (startTime >= blockStart && startTime < blockEnd) ||
              (endTime > blockStart && endTime <= blockEnd) ||
              (startTime <= blockStart && endTime >= blockEnd)
            );
          }
        });

        return !isRecurringlyBlocked;
      };

      // Generate time slots based on service duration
      const slots: TimeSlotAvailability[] = [];
      
      // Only show slots at service duration intervals
      for (let time = new Date(availabilityStart); time < availabilityEnd; time = new Date(time.getTime() + serviceDuration * 60000)) {
        // Check if there's enough time for the service before the end of availability
        const slotEndTime = new Date(time.getTime() + serviceDuration * 60000);
        if (slotEndTime > availabilityEnd) {
          continue; // Skip if the service can't be completed before closing
        }

        const availableCountersForSlot: number[] = [];

        // Check each counter's availability by scanning in 5-minute intervals
        for (const counterId of counterIds) {
          let isAvailable = true;
          
          // Scan the entire duration of the potential slot in 5-minute intervals
          for (let scanTime = new Date(time); scanTime < slotEndTime; scanTime = new Date(scanTime.getTime() + SCAN_INTERVAL * 60000)) {
            const scanEndTime = new Date(Math.min(scanTime.getTime() + SCAN_INTERVAL * 60000, slotEndTime.getTime()));
            if (!isSlotAvailable(scanTime, scanEndTime, counterId)) {
              isAvailable = false;
              break;
            }
          }

          if (isAvailable) {
            availableCountersForSlot.push(counterId);
          }
        }

        if (availableCountersForSlot.length > 0) {
          const timeString = time.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
          slots.push({
            time: timeString,
            availableCounters: availableCountersForSlot
          });
        }
      }

      setAvailableSlots(slots);

    } catch (error) {
      console.error('Error fetching available slots:', error);
      setError('Failed to load available time slots');
    }
  };

  const handleTimeSelection = async (time: string, availableCounters: number[]) => {
    setSelectedTime(time);
    // Select the first available counter by default
    const counterId = availableCounters[0];
    setSelectedCounterId(counterId);
    
    // Fetch counter name from service_counters
    try {
      const { data: counterData, error: counterError } = await supabase
        .from('service_counters')
        .select('name')
        .eq('id', counterId)
        .single();

      if (counterError) {
        console.error('Error fetching counter name:', counterError);
        setSelectedCounterName('Service Counter ' + counterId);
      } else if (counterData) {
        setSelectedCounterName(counterData.name);
      } else {
        setSelectedCounterName('Service Counter ' + counterId);
      }
    } catch (error) {
      console.error('Error fetching counter name:', error);
      setSelectedCounterName('Service Counter ' + counterId);
    }
  };

  const handleBooking = async () => {
    if (!selectedService || !selectedDate || !selectedTime || !user || selectedCounterId === null) {
      setError('Please select a service, date, and time to book.');
      return;
    }

    try {
      // Get the user's profile information
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id, contact_name, phone')
        .eq('user_id', user.id)
        .single();

      if (profileError) {
        throw profileError;
      }

      if (!userProfile) {
        throw new Error('User profile not found');
      }

      // Get all counters that can provide this service
      const { data: serviceCounters, error: counterError } = await supabase
        .from('counter_services')
        .select('counter_id')
        .eq('service_id', selectedService.id);

      if (counterError) {
        throw counterError;
      }

      if (!serviceCounters || serviceCounters.length === 0) {
        throw new Error('No counters available for this service');
      }

      const counterIds = serviceCounters.map(sc => sc.counter_id);

      // Parse the selected time and create appointment date
      const [timeStr, period] = selectedTime.split(' '); // Split "01:45 PM" into "01:45" and "PM"
      const [hoursStr, minutesStr] = timeStr.split(':');
      let hours = parseInt(hoursStr);
      const minutes = parseInt(minutesStr);
      
      // Convert to 24-hour format
      if (period === 'PM' && hours !== 12) {
        hours = hours + 12;
      } else if (period === 'AM' && hours === 12) {
        hours = 0;
      }

      // Create the appointment date/time - store exactly as selected
      const appointmentDateTime = new Date(`${selectedDate}T00:00:00`);
      appointmentDateTime.setHours(hours, minutes, 0, 0);
      const appointmentEndTime = new Date(appointmentDateTime.getTime() + (selectedService.duration || 30) * 60000);

      // Check for existing bookings at the selected time
      const { data: existingBookings } = await supabase
        .from('queue_entries')
        .select(`
          counter_id,
          scheduled_start_time,
          services!inner (
            duration
          )
        `)
        .in('counter_id', counterIds)
        .in('status', ['scheduled', 'in_progress'])
        .gte('scheduled_start_time', appointmentDateTime.toISOString())
        .lt('scheduled_start_time', appointmentEndTime.toISOString());

      // Check for blocked time slots
      const { data: blockedSlots } = await supabase
        .from('blocked_time_slots')
        .select('*')
        .eq('provider_id', provider_id)
        .eq('date', selectedDate)
        .in('counter_id', counterIds);

      // Check for recurring blocks
      const { data: recurringBlocks } = await supabase
        .from('recurring_blocks')
        .select('*')
        .eq('provider_id', provider_id)
        .eq('is_active', true);

      // Find an available counter
      let selectedCounter = null;
      for (const counterId of counterIds) {
        let isCounterAvailable = true;

        // Check existing bookings
        const hasBooking = existingBookings?.some((booking) => {
          const bookingStart = new Date(booking.scheduled_start_time.replace(' ', 'T'));
          const bookingEnd = new Date(bookingStart.getTime() + (booking.services[0]?.duration || 30) * 60000);
          return (
            booking.counter_id === counterId &&
            ((appointmentDateTime >= bookingStart && appointmentDateTime < bookingEnd) ||
             (appointmentEndTime > bookingStart && appointmentEndTime <= bookingEnd) ||
             (appointmentDateTime <= bookingStart && appointmentEndTime >= bookingEnd))
          );
        });

        if (hasBooking) {
          isCounterAvailable = false;
          continue;
        }

        // Check specific blocks
        const isSpecificallyBlocked = blockedSlots?.some((block) => {
          if (block.counter_id !== counterId) return false;
          const timeRange = parseTimeRange(block.time);
          if (!timeRange) return false;

          const blockStart = new Date(`${selectedDate}T00:00:00`);
          blockStart.setHours(timeRange.start.getHours(), timeRange.start.getMinutes(), 0, 0);
          
          const blockEnd = new Date(`${selectedDate}T00:00:00`);
          blockEnd.setHours(timeRange.end.getHours(), timeRange.end.getMinutes(), 0, 0);

          return (
            (appointmentDateTime >= blockStart && appointmentDateTime < blockEnd) ||
            (appointmentEndTime > blockStart && appointmentEndTime <= blockEnd) ||
            (appointmentDateTime <= blockStart && appointmentEndTime >= blockEnd)
          );
        });

        if (isSpecificallyBlocked) {
          isCounterAvailable = false;
          continue;
        }

        // Check recurring blocks
        const isRecurringlyBlocked = recurringBlocks?.some((block) => {
          if (block.counter_id !== null && block.counter_id !== counterId) return false;
          
          const dayOfWeek = appointmentDateTime.getDay();
          if (block.day_of_week !== null && parseInt(block.day_of_week) !== dayOfWeek) return false;

          const [blockStartHour, blockStartMinute] = block.start_time.split(':').map((num: string) => parseInt(num));
          const [blockEndHour, blockEndMinute] = block.end_time.split(':').map((num: string) => parseInt(num));
          
          const blockStart = new Date(`${selectedDate}T00:00:00`);
          blockStart.setHours(blockStartHour, blockStartMinute, 0, 0);
          
          const blockEnd = new Date(`${selectedDate}T00:00:00`);
          blockEnd.setHours(blockEndHour, blockEndMinute, 0, 0);

          if (blockStartHour > blockEndHour || (blockStartHour === blockEndHour && blockStartMinute > blockEndMinute)) {
            // Block goes past midnight
            const nextDayBlockEnd = new Date(blockEnd.getTime() + 24 * 60 * 60 * 1000);
            const prevDayBlockStart = new Date(blockStart.getTime() - 24 * 60 * 60 * 1000);
            return (
              (appointmentDateTime < nextDayBlockEnd && appointmentEndTime > blockStart) ||
              (appointmentDateTime < blockEnd && appointmentEndTime > prevDayBlockStart)
            );
          } else {
            return (appointmentDateTime < blockEnd && appointmentEndTime > blockStart);
          }
        });

        if (isRecurringlyBlocked) {
          isCounterAvailable = false;
          continue;
        }

        if (isCounterAvailable) {
          selectedCounter = counterId;
          break;
        }
      }

      if (!selectedCounter) {
        throw new Error('No counters available at the selected time. Please choose a different time.');
      }

      // Get queue configuration
      const { data: queueConfigs, error: configError } = await supabase
        .from('queue_configurations')
        .select('prefix, current_number')
        .eq('provider_id', provider_id);

      if (configError) {
        throw configError;
      }

      // Use the first config or create default values
      const configData = queueConfigs?.[0] || { prefix: '', current_number: 1 };
      const prefix = configData.prefix || '';
      const currentNumber = configData.current_number || 1;

      // Format queue number as MA43 (prefix + current number)
      const queueNumber = `${prefix}${currentNumber}`;

      // Prepare the entry data - store the time exactly as selected
      const entryData = {
        service_id: selectedService.id,
        counter_id: selectedCounterId,
        provider_id: provider_id,
        user_id: user.id,
        customer_name: userProfile.contact_name || user.email,
        customer_phone: userProfile.phone || null,
        queue_number: queueNumber,
        status: 'scheduled' as const,
        notes: null,
        entry_type: 'appointment' as const,
        entry_source: 'online' as const,
        service_duration_minutes: selectedService.duration,
        scheduled_start_time: `${appointmentDateTime.getFullYear()}-${String(appointmentDateTime.getMonth() + 1).padStart(2, '0')}-${String(appointmentDateTime.getDate()).padStart(2, '0')} ${String(appointmentDateTime.getHours()).padStart(2, '0')}:${String(appointmentDateTime.getMinutes()).padStart(2, '0')}:00`,
        actual_start_serving_at: null,
        actual_completed_at: null
      };

      // Insert the queue entry
      const { data: booking, error: bookingError } = await supabase
        .from('queue_entries')
        .insert([entryData])
        .select()
        .single();

      if (bookingError) {
        throw bookingError;
      }

      // Create notification for the booking
      let notificationSuccess = false;
      if (user?.id) {
        try {
          console.log('[BookingFlow] Creating booking notification:', {
            userId: user.id,
            bookingId: booking.id,
            serviceName: selectedService?.name,
            scheduledTime: booking.scheduled_start_time,
            queueNumber: booking.queue_number,
            timeFormat // Pass the current time format preference
          });

          const notificationResults = await notificationService.createBookingNotification(
            user.id,
            booking.id,
            selectedService?.name || 'Service',
            appointmentDateTime,
            booking.queue_number,
            timeFormat // Pass the current time format preference
          );
          
          if (notificationResults) {
            console.log('[BookingFlow] Notification created successfully');
            notificationSuccess = true;
          } else {
            console.error('[BookingFlow] Failed to create notifications');
          }
        } catch (error) {
          console.error('[BookingFlow] Error creating notifications:', error);
          if (error instanceof Error) {
            console.error('[BookingFlow] Error details:', {
              message: error.message,
              stack: error.stack,
              name: error.name
            });
          }
        }
      }

      setBookingConfirmation({
        ...booking,
        scheduled_start_time: appointmentDateTime,
        notificationEnabled: notificationSuccess
      });
      setShowConfirmation(true);

    } catch (error) {
      console.error('Error creating booking:', error);
      setError(error instanceof Error ? error.message : 'Failed to create booking. Please try again.');
    }
  };

  const toggleWeekView = () => {
    setShowingNextWeek(!showingNextWeek);
    setSelectedDate(''); // Clear selected date when switching weeks
  };

  const padNumber = (num: number): string => {
    return num.toString().padStart(2, '0');
  };

  const handleBackFromConfirmation = () => {
    setShowConfirmation(false);
    setBookingConfirmation(null);
    setSelectedDate('');
    setSelectedTime('');
  };

  // Add calendar toggle button in the header section
  const renderCalendarToggle = () => (
    <div className="flex items-center gap-2 mb-4">
      <span className="text-sm font-medium">{t('calendarType')}:</span>
      <button
        onClick={() => setTimeFormat(timeFormat === 'gregorian' ? 'ethiopian' : 'gregorian')}
        className="px-3 py-1 text-sm rounded-md border border-gray-300 hover:bg-gray-50"
      >
        {timeFormat === 'ethiopian' ? t('switchToGregorian') : t('switchToEthiopian')}
      </button>
    </div>
  );

  // Modify date display in the date selection section
  const renderDateButton = (dateInfo: { date: string; dayName: string; dayOfMonth: number; isToday?: boolean }) => {
    const date = new Date(dateInfo.date);
    const ethiopianDate = getEthiopianDateStr(date);
    
    return (
      <button
        key={dateInfo.date}
        onClick={() => {
          setSelectedDate(dateInfo.date);
          setSelectedTime('');
        }}
        className={`p-3 rounded-lg border text-sm ${
          selectedDate === dateInfo.date
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-200 hover:border-primary-500'
        }`}
      >
        <div className="text-center">
          <div className="text-gray-500 mb-1">{dateInfo.dayName}</div>
          <div className={`font-semibold ${dateInfo.isToday ? 'text-primary-600' : ''}`}>
            {timeFormat === 'gregorian' ? (
              <>
                {dateInfo.dayOfMonth}
                {dateInfo.isToday && <span className="text-xs ml-1">({t('today')})</span>}
              </>
            ) : (
              <div className="text-sm">
                <div>{ethiopianDate}</div>
                {dateInfo.isToday && <span className="text-xs">(Today)</span>}
              </div>
            )}
          </div>
        </div>
      </button>
    );
  };

  // Modify time slot display
  const renderTimeSlot = (slot: TimeSlotAvailability) => {
    const [timeStr, period] = slot.time.split(' ');
    const [hoursStr, minutesStr] = timeStr.split(':');
    const hours = parseInt(hoursStr);
    const minutes = parseInt(minutesStr);
    const is24Hour = period === 'PM' && hours !== 12 ? hours + 12 : hours;

    return (
      <button
        key={slot.time}
        onClick={() => handleTimeSelection(slot.time, slot.availableCounters)}
        className={`p-3 rounded-lg border text-sm ${
          selectedTime === slot.time
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-200 hover:border-primary-500'
        }`}
      >
        <div className="text-center">
          <div>{getTimeStr(is24Hour, minutes, false, timeFormat)}</div>
          <div className="text-xs text-gray-500">
            {slot.availableCounters.length > 1 
              ? `${slot.availableCounters.length} ${t('countersAvailable')}`
              : `1 ${t('counterAvailable')}`}
          </div>
        </div>
      </button>
    );
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="flex justify-center items-center min-h-screen text-red-600">{error}</div>;
  }

  if (showConfirmation && bookingConfirmation) {
    // Format the date and time based on selected calendar
    const bookingDate = bookingConfirmation.scheduled_start_time;
    const formattedDateTime = timeFormat === 'ethiopian' 
      ? `${getEthiopianDateStr(bookingDate)} ${getTimeStr(
          bookingDate.getHours(),
          bookingDate.getMinutes(),
          false,
          'ethiopian'
        )}`
      : bookingDate.toLocaleString();

    return (
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('bookingConfirmed')}</h1>
          <p className="text-gray-600">
            {timeFormat === 'ethiopian' 
              ? t('appointmentScheduledEth', { 
                  service: selectedService?.name || t('service'),
                  dateTime: formattedDateTime
                })
              : t('appointmentScheduled')}
          </p>
          
          {/* Notification Status */}
          {!bookingConfirmation.notificationEnabled && (
            <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
              <p className="text-yellow-800 text-sm">
                {t('note')}
              </p>
              <button
                onClick={async () => {
                  try {
                    console.log('[BookingFlow] Retrying notification setup...');
                    const notificationResults = await notificationService.createBookingNotification(
                      user?.id!,
                      bookingConfirmation.id,
                      selectedService?.name || 'Service',
                      bookingDate,
                      bookingConfirmation.queue_number,
                      timeFormat // Pass the current time format preference
                    );
                    
                    if (notificationResults) {
                      console.log('[BookingFlow] Manual retry successful');
                      setBookingConfirmation({
                        ...bookingConfirmation,
                        notificationEnabled: true
                      });
                    } else {
                      console.error('[BookingFlow] Manual retry failed - null result');
                    }
                  } catch (error) {
                    console.error('[BookingFlow] Error in manual notification retry:', error);
                  }
                }}
                className="mt-2 text-sm text-yellow-800 underline hover:no-underline"
              >
                {t('tryEnablingNotificationsAgain')}
              </button>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="flex justify-center mb-6">
            <QRCode
              value={bookingConfirmation.id}
              size={128}
              style={{ height: "auto", maxWidth: "100%", width: "128px" }}
              viewBox={`0 0 128 128`}
            />
          </div>

          <div className="border-t border-b border-gray-200 py-6">
            <dl className="grid grid-cols-1 gap-4">
              <div className="grid grid-cols-3 gap-4">
                <dt className="text-gray-500">{t('queueNumber')}</dt>
                <dd className="text-gray-900 col-span-2 font-semibold">{bookingConfirmation.queue_number}</dd>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <dt className="text-gray-500">{t('service')}</dt>
                <dd className="text-gray-900 col-span-2">{selectedService?.name}</dd>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <dt className="text-gray-500">{t('counter')}</dt>
                <dd className="text-gray-900 col-span-2">{selectedCounterName}</dd>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <dt className="text-gray-500">{t('dateAndTime')}</dt>
                <dd className="text-gray-900 col-span-2">{formattedDateTime}</dd>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <dt className="text-gray-500">{t('notifications')}</dt>
                <dd className="text-gray-900 col-span-2">
                  {bookingConfirmation.notificationEnabled ? (
                    <span className="text-green-600 flex items-center">
                      <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      {t('enabled')}
                    </span>
                  ) : (
                    <span className="text-yellow-600">{t('notEnabled')}</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={handleBackFromConfirmation}
              className="inline-flex items-center justify-center px-6 py-3 border border-primary-600 text-primary-600 font-medium rounded-md hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {t('bookAnotherAppointment')}
            </button>
            <Link
              to="/profile"
              className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('backToProfile')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Book an Appointment</h1>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-8">
          {/* Header with Back Button and Calendar Toggle */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => history.goBack()}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5 mr-2" 
                viewBox="0 0 20 20" 
                fill="currentColor"
              >
                <path 
                  fillRule="evenodd" 
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" 
                  clipRule="evenodd" 
                />
              </svg>
              Back
            </button>
            <h1 className="text-2xl font-bold text-center flex-1 mr-12">{t('bookAppointment')}</h1>
          </div>

          {/* Calendar Toggle */}
          {renderCalendarToggle()}

          {/* Service Selection */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">{t('selectService')}</h2>
            <div className="grid grid-cols-1 gap-2">
              {services.map((service) => (
                <button
                  key={service.id}
                  onClick={() => setSelectedService(service)}
                  className={`p-4 text-left rounded-lg border transition-all ${
                    selectedService?.id === service.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-primary-500'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-medium">{service.name}</h3>
                      <p className="text-sm text-gray-500">{service.duration} minutes</p>
                    </div>
                    <span className="font-semibold">${service.price}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Book Now Button - Moved here, inside service selection section */}
            {selectedService && (
              <div className="mt-6">
                <button
                  onClick={handleBooking}
                  className={`w-full px-8 py-4 rounded-lg text-lg font-medium transition-colors ${
                    selectedDate && selectedTime
                      ? 'bg-primary-600 hover:bg-primary-700 text-white'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  disabled={!selectedDate || !selectedTime}
                >
                  {selectedDate && selectedTime ? t('bookNow') : t('selectDateAndTime')}
                </button>
              </div>
            )}
          </div>

          {/* Date Selection */}
          {selectedService && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">{t('selectDate')}</h2>
                <button
                  onClick={toggleWeekView}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  {showingNextWeek ? t('currentWeek') : t('nextWeek')}
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {(showingNextWeek ? nextWeekDates : availableDates).map(dateInfo => renderDateButton(dateInfo))}
              </div>
            </div>
          )}

          {/* Time Selection */}
          {selectedDate && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('selectTime')}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {availableSlots.map(slot => renderTimeSlot(slot))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingFlow;